import type { Paper, Keyword, AnalysisResults, DuplicateGroup } from '../types';

// Normalize text for matching
function normalizeText(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/\b'\b/g, '') // Remove apostrophes in words
    .replace(/[^\w\s]/g, ' ') // Replace punctuation with spaces
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
}

// Find matching keywords in text
function findMatchingKeywords(
  text: string,
  keywords: Keyword[],
  options: { caseSensitive: boolean; matchWholeWords: boolean }
): string[] {
  if (!text || keywords.length === 0) return [];

  const normalizedText = options.caseSensitive ? text : normalizeText(text);
  const textWords = new Set(normalizedText.split(' '));
  const matches: string[] = [];

  for (const keyword of keywords) {
    const normalizedKeyword = options.caseSensitive
      ? keyword.text
      : normalizeText(keyword.text);

    if (!normalizedKeyword) continue;

    const keywordWords = normalizedKeyword.split(' ');

    if (options.matchWholeWords) {
      // All words must be present as whole words
      if (keywordWords.every((word) => textWords.has(word))) {
        matches.push(keyword.text);
      }
    } else {
      // Phrase match or all words present
      if (normalizedText.includes(normalizedKeyword) ||
          keywordWords.every((word) => textWords.has(word))) {
        matches.push(keyword.text);
      }
    }
  }

  return matches;
}

// Analyze a single paper
function analyzePaper(
  paper: Paper,
  inclusionKeywords: Keyword[],
  exclusionKeywords: Keyword[],
  options: { caseSensitive: boolean; matchWholeWords: boolean; searchInAbstract: boolean }
): Paper {
  // Find matches in title
  const titleInclusionMatches = findMatchingKeywords(paper.title, inclusionKeywords, options);
  const titleExclusionMatches = findMatchingKeywords(paper.title, exclusionKeywords, options);

  // Find matches in abstract (if enabled and abstract exists)
  let abstractInclusionMatches: string[] = [];
  let abstractExclusionMatches: string[] = [];
  if (options.searchInAbstract && paper.abstract) {
    abstractInclusionMatches = findMatchingKeywords(paper.abstract, inclusionKeywords, options);
    abstractExclusionMatches = findMatchingKeywords(paper.abstract, exclusionKeywords, options);
  }

  // Combine unique matches
  const allInclusionMatches = Array.from(new Set([...titleInclusionMatches, ...abstractInclusionMatches]));
  const allExclusionMatches = Array.from(new Set([...titleExclusionMatches, ...abstractExclusionMatches]));

  // Track where each keyword was found
  const matchLocations: { keyword: string; location: 'title' | 'abstract' | 'both' }[] = [];

  const allKeywords = Array.from(new Set([...allInclusionMatches, ...allExclusionMatches]));
  for (const kw of allKeywords) {
    const inTitle = titleInclusionMatches.includes(kw) || titleExclusionMatches.includes(kw);
    const inAbstract = abstractInclusionMatches.includes(kw) || abstractExclusionMatches.includes(kw);

    if (inTitle && inAbstract) {
      matchLocations.push({ keyword: kw, location: 'both' });
    } else if (inTitle) {
      matchLocations.push({ keyword: kw, location: 'title' });
    } else if (inAbstract) {
      matchLocations.push({ keyword: kw, location: 'abstract' });
    }
  }

  let category: Paper['category'];
  if (allInclusionMatches.length > 0 && allExclusionMatches.length > 0) {
    category = 'both';
  } else if (allInclusionMatches.length > 0) {
    category = 'inclusion';
  } else if (allExclusionMatches.length > 0) {
    category = 'exclusion';
  } else {
    category = 'none';
  }

  return {
    ...paper,
    category,
    matchedInclusionKeywords: allInclusionMatches,
    matchedExclusionKeywords: allExclusionMatches,
    matchLocations,
  };
}

// Main analysis function
export async function analyzePapers(
  papers: Paper[],
  inclusionKeywords: Keyword[],
  exclusionKeywords: Keyword[],
  options: {
    caseSensitive: boolean;
    matchWholeWords: boolean;
    searchInAbstract: boolean;
    onProgress?: (current: number, total: number) => void;
  }
): Promise<AnalysisResults> {
  const startTime = performance.now();

  const inclusionOnly: Paper[] = [];
  const exclusionOnly: Paper[] = [];
  const both: Paper[] = [];
  const none: Paper[] = [];

  // Process papers in batches for better performance
  const batchSize = 100;
  const analyzedPapers: Paper[] = [];

  for (let i = 0; i < papers.length; i += batchSize) {
    const batch = papers.slice(i, i + batchSize);

    for (const paper of batch) {
      const analyzed = analyzePaper(paper, inclusionKeywords, exclusionKeywords, {
        caseSensitive: options.caseSensitive,
        matchWholeWords: options.matchWholeWords,
        searchInAbstract: options.searchInAbstract,
      });

      analyzedPapers.push(analyzed);

      // Categorize
      switch (analyzed.category) {
        case 'inclusion':
          inclusionOnly.push(analyzed);
          break;
        case 'exclusion':
          exclusionOnly.push(analyzed);
          break;
        case 'both':
          both.push(analyzed);
          break;
        case 'none':
          none.push(analyzed);
          break;
      }
    }

    // Report progress
    if (options.onProgress) {
      options.onProgress(Math.min(i + batchSize, papers.length), papers.length);
    }

    // Yield to prevent UI blocking
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  const endTime = performance.now();

  return {
    total: papers.length,
    inclusionOnly,
    exclusionOnly,
    both,
    none,
    timestamp: new Date(),
    processingTime: endTime - startTime,
  };
}

// Highlight keywords in text
export function highlightKeywords(
  text: string,
  inclusionKeywords: string[],
  exclusionKeywords: string[],
  options: { caseSensitive: boolean } = { caseSensitive: false }
): { html: string; hasHighlights: boolean } {
  if (!text) return { html: '', hasHighlights: false };

  let result = text;
  let hasHighlights = false;

  // Helper to create regex
  const createRegex = (keyword: string) => {
    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(${escaped})`, options.caseSensitive ? 'g' : 'gi');
  };

  // Highlight exclusion keywords (red)
  for (const keyword of exclusionKeywords) {
    const regex = createRegex(keyword);
    if (regex.test(result)) {
      hasHighlights = true;
      result = result.replace(
        regex,
        '<mark class="highlight-exclusion">$1</mark>'
      );
    }
  }

  // Highlight inclusion keywords (green) - avoid double highlighting
  for (const keyword of inclusionKeywords) {
    if (exclusionKeywords.some((e) =>
      options.caseSensitive ? e === keyword : e.toLowerCase() === keyword.toLowerCase()
    )) {
      continue; // Skip if already highlighted as exclusion
    }

    const regex = createRegex(keyword);
    if (regex.test(result)) {
      hasHighlights = true;
      result = result.replace(
        regex,
        '<mark class="highlight-inclusion">$1</mark>'
      );
    }
  }

  return { html: result, hasHighlights };
}

// Get summary statistics
export function getAnalysisSummary(results: AnalysisResults): {
  percentages: Record<string, number>;
  topKeywords: { keyword: string; count: number; type: 'inclusion' | 'exclusion' }[];
} {
  const percentages = {
    inclusionOnly: (results.inclusionOnly.length / results.total) * 100,
    exclusionOnly: (results.exclusionOnly.length / results.total) * 100,
    both: (results.both.length / results.total) * 100,
    none: (results.none.length / results.total) * 100,
  };

  // Count keyword occurrences
  const keywordCounts = new Map<string, { count: number; type: 'inclusion' | 'exclusion' }>();

  const allPapers = [...results.inclusionOnly, ...results.exclusionOnly, ...results.both];

  for (const paper of allPapers) {
    for (const kw of paper.matchedInclusionKeywords) {
      const existing = keywordCounts.get(kw);
      if (existing) {
        existing.count++;
      } else {
        keywordCounts.set(kw, { count: 1, type: 'inclusion' });
      }
    }
    for (const kw of paper.matchedExclusionKeywords) {
      const existing = keywordCounts.get(kw);
      if (existing) {
        existing.count++;
      } else {
        keywordCounts.set(kw, { count: 1, type: 'exclusion' });
      }
    }
  }

  const topKeywords = Array.from(keywordCounts.entries())
    .map(([keyword, data]) => ({ keyword, ...data }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  return { percentages, topKeywords };
}

// Detect duplicates in papers
export function detectDuplicates(papers: Paper[]): {
  uniquePapers: Paper[];
  duplicateGroups: DuplicateGroup[];
  totalDuplicates: number;
} {
  const uniquePapers: Paper[] = [];
  const duplicateGroups: DuplicateGroup[] = [];

  // Track seen papers by PMID, DOI, and normalized title
  const seenByPmid = new Map<string, Paper>();
  const seenByDoi = new Map<string, Paper>();
  const seenByTitle = new Map<string, Paper>();

  // Track duplicate groups
  const duplicateGroupMap = new Map<string, DuplicateGroup>();

  for (const paper of papers) {
    let isDuplicate = false;
    let duplicateOf: Paper | undefined;
    let reason: 'pmid' | 'doi' | 'title' | undefined;

    // Check by PMID first (most reliable)
    if (paper.pmid && seenByPmid.has(paper.pmid)) {
      isDuplicate = true;
      duplicateOf = seenByPmid.get(paper.pmid);
      reason = 'pmid';
    }
    // Check by DOI
    else if (paper.doi && seenByDoi.has(paper.doi.toLowerCase())) {
      isDuplicate = true;
      duplicateOf = seenByDoi.get(paper.doi.toLowerCase());
      reason = 'doi';
    }
    // Check by normalized title (fuzzy match)
    else {
      const normalizedTitle = normalizeText(paper.title);
      if (normalizedTitle && seenByTitle.has(normalizedTitle)) {
        isDuplicate = true;
        duplicateOf = seenByTitle.get(normalizedTitle);
        reason = 'title';
      }
    }

    if (isDuplicate && duplicateOf && reason) {
      // Mark paper as duplicate
      const duplicatePaper: Paper = {
        ...paper,
        isDuplicate: true,
        duplicateOf: duplicateOf.pmid,
        duplicateReason: reason,
      };

      // Add to or create duplicate group
      const groupKey = duplicateOf.pmid;
      if (duplicateGroupMap.has(groupKey)) {
        duplicateGroupMap.get(groupKey)!.duplicates.push(duplicatePaper);
      } else {
        duplicateGroupMap.set(groupKey, {
          originalPmid: duplicateOf.pmid,
          originalTitle: duplicateOf.title,
          duplicates: [duplicatePaper],
          reason,
        });
      }
    } else {
      // Not a duplicate - add to tracking maps
      if (paper.pmid) {
        seenByPmid.set(paper.pmid, paper);
      }
      if (paper.doi) {
        seenByDoi.set(paper.doi.toLowerCase(), paper);
      }
      const normalizedTitle = normalizeText(paper.title);
      if (normalizedTitle) {
        seenByTitle.set(normalizedTitle, paper);
      }

      uniquePapers.push({
        ...paper,
        isDuplicate: false,
      });
    }
  }

  const duplicateGroupsArray = Array.from(duplicateGroupMap.values());
  const totalDuplicates = duplicateGroupsArray.reduce((sum, g) => sum + g.duplicates.length, 0);

  return {
    uniquePapers,
    duplicateGroups: duplicateGroupsArray,
    totalDuplicates,
  };
}
